// skipcq: JS-W1028, JS-0128
import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
//hooks
import useBrewerySnapshot from '../../hooks/useBrewerySnapshot';
import { useCompanyLocationOverride } from '../../hooks/useCompanyLocationOverride';
import { useBreweryDelete } from '../../hooks/useBreweryDelete';
import { useBreweryUpdate } from '../../hooks/useBreweryUpdate';
// import { useParams } from 'react-router-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
// material ui
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
// components
import Alert from '../../components/widgets/Alert';
import BreweryActionBar from './BreweryActionBar';
import DashboardLayout from '../../components/layout/DashboardLayout';
import BreweryDetailsTabs from './BreweryDetailsTabs';
import { BREWERY_MAX_TAB_INDEX } from '../../constants/breweryBeersTabIndex';

function Brewery() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    brewery,
    isLoading: breweryLoading,
    exists: breweryExists,
    error: breweryLoadError,
  } = useBrewerySnapshot(id);
  const hadFirestoreDocRef = useRef(false);
  const {
    override,
    isLoading: overrideLoading,
    error: overrideLoadError,
    reload: reloadOverride,
  } = useCompanyLocationOverride(id);
  const pageTitle = 'Brewery Manager';
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  useEffect(() => {
    const initialTab = location.state?.initialTabIndex;
    if (typeof initialTab === 'number' && initialTab >= 0 && initialTab <= BREWERY_MAX_TAB_INDEX) {
      setActiveTabIndex(initialTab);
    }
  }, [location.key, location.state?.initialTabIndex]);

  // Use custom hooks for business logic
  const {
    canDelete,
    deleteLoading,
    deleteBrewery: executeDelete,
    checkDeleteEligibility,
    errorMessage: deleteErrorMessage,
    preflightError,
    preflightLoading,
    counts,
  } = useBreweryDelete(id);
  const overrideLoaded = !overrideLoading && !overrideLoadError;
  const { updateBrewery } = useBreweryUpdate(
    id,
    brewery,
    overrideLoaded ? override : undefined,
  );

  useEffect(() => {
    hadFirestoreDocRef.current = false;
  }, [id]);

  useEffect(() => {
    if (breweryExists === true && brewery) {
      hadFirestoreDocRef.current = true;
    }
  }, [breweryExists, brewery]);

  // Only redirect with deletedBreweryId when the doc existed and then disappeared (e.g. deleted while viewing).
  // Stale Typesense / missing doc on first load shows an error instead so index drift stays visible in the list.
  useEffect(() => {
    if (
      !id ||
      breweryLoading ||
      breweryExists !== false ||
      !hadFirestoreDocRef.current ||
      isNavigating
    ) {
      return;
    }
    setIsNavigating(true);
    navigate('/breweries', {
      replace: true,
      state: { deletedBreweryId: id },
    });
  }, [breweryLoading, breweryExists, id, navigate, isNavigating]);

  const handleBackNavigation = useCallback(() => {
    navigate('/breweries');
  }, [navigate]);

  const handleCopyBreweryId = useCallback(() => {
    if (id && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(id);
    }
  }, [id]);

  const handleRetryDeletePreflight = useCallback(() => {
    checkDeleteEligibility();
  }, [checkDeleteEligibility]);

  const handleCloseSnackbar = useCallback(() => {
    setSuccessOpen(false);
  }, [setSuccessOpen]);

  const handleCloseErrorSnackbar = useCallback(() => {
    setErrorOpen(false);
  }, [setErrorOpen]);

  const handleChangeLogo = useCallback(() => {
    navigate(`/brewery/logo/edit/${id}`);
  }, [navigate, id]);

  const handleDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteDialogClose = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    setDeleteDialogOpen(false);
    const result = await executeDelete();
    if (result?.deleted) {
      // Navigate immediately to prevent rendering deleted brewery
      // Pass deleted brewery ID in state to optimistically remove it from list
      setIsNavigating(true);
      navigate('/breweries', {
        replace: true,
        state: { deletedBreweryId: id },
      });
    } else if (result?.error) {
      // Show error message if delete failed
      setErrorOpen(true);
    }
  }, [executeDelete, navigate, id]);

  const deleteTooltip = useMemo(() => {
    if (preflightLoading) return '';
    const items = [
      ['Beers', counts?.beers],
      ['Beer Reviews', counts?.beerReviews],
      ['Hospitality Reviews', counts?.hospitalityReviews],
      ['Log entries', counts?.checkIns],
      ['User Favorites', counts?.userFavsDocs],
    ]
      .filter(([, v]) => (typeof v === 'number' ? v > 0 : Boolean(v)))
      .map(([label, value]) => `${label}: ${value}`);
    return items.join(', ');
  }, [counts, preflightLoading]);

  /* function to update document in firestore
   *
   *  @param {object} values - the values object from formik
   *  @param {object} formikHelpers - Formik helpers object
   */
  const handleUpdate = useCallback(
    async (values, formikHelpers) => {
      const result = await updateBrewery(values, formikHelpers);
      if (result?.success) {
        setSuccessOpen(true);
      }
    },
    [updateBrewery],
  );

  if (isNavigating) {
    return null;
  }

  const showMissingFirestoreUi =
    !breweryLoading &&
    breweryExists === false &&
    !breweryLoadError &&
    !hadFirestoreDocRef.current;

  const pendingDeletionRedirect =
    !breweryLoading &&
    breweryExists === false &&
    !breweryLoadError &&
    hadFirestoreDocRef.current;

  const deletePreflightAlert =
    preflightError != null ? (
      <Box sx={{ px: 2, pt: 2, maxWidth: 960, mx: 'auto' }}>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleRetryDeletePreflight}
              disabled={preflightLoading}
            >
              Retry check
            </Button>
          }
        >
          <Typography variant="body2" component="span">
            {preflightError.summary}
          </Typography>
          {preflightError.detail ? (
            <Typography
              variant="caption"
              color="text.secondary"
              component="div"
              sx={{ mt: 1, display: 'block' }}
            >
              {preflightError.detail}
            </Typography>
          ) : null}
        </Alert>
      </Box>
    ) : null;

  return (
    <DashboardLayout title={pageTitle}>
      {breweryLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh"
          }}>
          <CircularProgress />
        </Box>
      ) : breweryLoadError ? (
        <Box sx={{ maxWidth: 640, mx: 'auto', mt: 4, px: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not load this brewery from the database. Check your connection
            and permissions, then try again.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {breweryLoadError?.message || String(breweryLoadError)}
          </Typography>
          <Button variant="contained" onClick={handleBackNavigation}>
            Back to breweries
          </Button>
        </Box>
      ) : showMissingFirestoreUi ? (
        <Box sx={{ maxWidth: 640, mx: 'auto', mt: 4, px: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            No brewery document exists in Firestore for this ID. If it still
            appears in the brewery search list, the search index is likely stale
            or out of sync—investigate indexing or sync jobs rather than
            treating this as a normal delete.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Brewery ID:{' '}
            <Typography component="span" variant="body2" fontFamily="monospace">
              {id}
            </Typography>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            <Button variant="outlined" onClick={handleCopyBreweryId}>
              Copy ID
            </Button>
            <Button variant="contained" onClick={handleBackNavigation}>
              Back to breweries
            </Button>
          </Box>
          {deletePreflightAlert}
        </Box>
      ) : pendingDeletionRedirect ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {deletePreflightAlert}
          <Snackbar
            open={successOpen}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            severity="success"
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity="success"
              sx={{ width: '100%' }}
            >
              Changes saved successfully
            </Alert>
          </Snackbar>
          <Snackbar
            open={errorOpen}
            autoHideDuration={6000}
            onClose={handleCloseErrorSnackbar}
          >
            <Alert
              onClose={handleCloseErrorSnackbar}
              severity="error"
              sx={{ width: '100%' }}
            >
              {deleteErrorMessage}
            </Alert>
          </Snackbar>
          <BreweryDetailsTabs
            brewery={brewery}
            override={override}
            overrideLoading={overrideLoading}
            overrideLoadError={overrideLoadError}
            onRetryOverrideLoad={reloadOverride}
            handleUpdate={handleUpdate}
            handleBackNavigation={handleBackNavigation}
            parentBreweryId={id}
            onSaveStateChange={setIsSaveEnabled}
            value={activeTabIndex}
            onTabChange={setActiveTabIndex}
            detailsActionBar={
              <BreweryActionBar
                formId="brewery-override-form"
                onChangeLogo={handleChangeLogo}
                saveDisabled={!isSaveEnabled || overrideLoading || Boolean(overrideLoadError)}
                onDelete={handleDeleteClick}
                deleteDisabled={!canDelete || deleteLoading || preflightLoading}
                deleteBusy={preflightLoading}
                deleteTooltip={deleteTooltip}
                showDeleteInfo={
                  !canDelete && !preflightLoading && Boolean(deleteTooltip)
                }
              />
            }
          />
          <Dialog
            open={deleteDialogOpen}
            onClose={handleDeleteDialogClose}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
          >
            <DialogTitle id="delete-dialog-title">Delete Brewery</DialogTitle>
            <DialogContent>
              <DialogContentText id="delete-dialog-description">
                Are you sure you want to delete this brewery? This action cannot
                be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDeleteDialogClose} color="primary">
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                color="error"
                variant="contained"
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </DashboardLayout>
  );
}

export default Brewery;
